import { Component, Input, Type } from '@angular/core';

import { Observable, BehaviorSubject } from 'rxjs';

import { DropdownOptionModel } from '../../../Forms/models/dropdown-option.model';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { JournodexSourceTypeEnum } from '../../../../Monitor/Shared/enums/journodex-source-type.enum';
import { SourceGroup } from '../../../../Common/Sources/models/source-group.model';
import { SourceGroupService } from '../../../../Common/Sources/services/source-group.service';
import { SearchCriteriaSourcesEnum } from '../../../../api/mentions/enum/search-criteria-sources.enum';
import { FullProfileDialogAnimationsEnum } from '../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../../Core/Dialogs/enums/overlay-animation.enum';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { ControlValueAccessorModel, getProvider } from '../../../Forms/models/control-value-accessor.model';
import { ActionKeys } from '../../../Forms/enums/keys.enum';
import { JournodexSourceModel } from '../../../../Common/Journodex/models/journodex-source.model';


interface DataInterfaceModel {
    dropdownValue: {
        id: string;
        name: string;
        disableTextbox: boolean;
        sourceId: number;
    };
    textboxValue: string;
}

interface Chip {
    entityType: string;
    entityName: string;
    displayString: string;
}

// @TODO this component needs some serious cleaning up

@Component({
    selector: 'agpr-include-exclude-source-picker',
    templateUrl: './include-exclude-source-picker.component.html',
    styleUrls: ['./include-exclude-source-picker.component.scss'],
    providers: [getProvider(IncludeExcludeSourcePickerComponent)],
    standalone: false
})
export class IncludeExcludeSourcePickerComponent extends ControlValueAccessorModel<JournodexSourceModel[]> {
    @Input() id: string = null;
    @Input() addOnCustomKeys: ActionKeys[] = [];
    @Input() disabled: boolean = false;
    @Input() tabindex: number = null;
    @Input() max: number = null;
    @Input() field: string = null;
    @Input() placeholder: string = '';
    @Input() styleClass: string = null;
    @Input() inputStyleClass: string = null;

    @Input() set dropdownChoices(options: any[]) {
        this._dropdownChoices = options;
        this.updateDropdownOptions();
        if (this.dropdownOptions.length !== 0){
            this.dropdownOption = this.dropdownOptions[0];
        }
        this.updatePlaceholder();
    }
    @Input() showManageIcon: boolean = false;
    @Input() ariaInvalid: boolean = false;
    @Input() ariaDescribedBy: string;

    @Input() manageDialog: Type<AbstractDialogComponent>;
    @Input() dropdownAriaLabel: string;

    _dropdownChoices: any[] = [];
    dropdownOptions: DropdownOptionModel[];
    dropdownOption: DropdownOptionModel;
    formData: DataInterfaceModel = null;
    holdingBoxForDisablingDropdownItem: any = null;
    currentDropdownSelector: any = null;
    chips: Chip[] = [];
    showAutocomplete: boolean = false;
    sourceGroupList$: Observable<SourceGroup[]>;
    selectedSourceGroup: SourceGroup;
    _placeholder: string;

    readonly buttonTypes = ButtonTypesEnum;
    readonly sourceTypes = SearchCriteriaSourcesEnum;

    public typeToStringMap = {
        [SearchCriteriaSourcesEnum.journalist]: '(CON)',
        [SearchCriteriaSourcesEnum.outlet]: '(OUT)',
        [SearchCriteriaSourcesEnum.urls]: '(URL)',
        [SearchCriteriaSourcesEnum.sourceGroups]: '(GRU)',
    };

    constructor(
        public coreDialogService: CoreDialogService,
        private sourceGroupService: SourceGroupService,
    ) {
        super();
        this.value = this.value || [];
        this.formData = {
            dropdownValue: null,
            textboxValue: null
        };
        this.disabled = true;
        this.holdingBoxForDisablingDropdownItem = null;
        this.sourceGroupList$ = this.sourceGroupService.all$;
    }


    writeValue(value: JournodexSourceModel[]): void {
        if (!this.formData) {
            this.formData = {
                dropdownValue: null,
                textboxValue: null
            };
        }
        this.value = value || [];
        this.chips = this.value.slice();
    }


    /**
     * Adds a new chip if the key pressed as one of the specified triggers
     */
    onKeyDown(event: KeyboardEvent): void {
        const enterKey = event.key === ActionKeys.ENTER;
        const customKey = this.addOnCustomKeys.indexOf(event.key as ActionKeys) > -1;

        if (!enterKey && !customKey || !this.formData) {
            return;
        }

        event.preventDefault();

        this.addChips();
    }


    addChips(): void {
        const hasDropdown = this.formData.dropdownValue !== null && this.formData.dropdownValue.id !== null;

        if (!this.formData.textboxValue || !this.formData.textboxValue.trim().length) {
            return;
        }

        // If the passed form data didn't include a proper dropdown item, use the last one we cached for this purpose
        if (!hasDropdown) {
            this.formData.dropdownValue = this.currentDropdownSelector;
        }

        const splitOnPattern: RegExp = new RegExp('[,;]');
        const chips: string[] = this.formData.textboxValue.split(splitOnPattern);
        chips.forEach(chipValue => {
            const data: DataInterfaceModel = {
                dropdownValue: this.formData.dropdownValue,
                textboxValue: chipValue.trim()
            };
            this.addChip(data);
        });
    }

    /**
     * Adds a new chip to the array of existing chips
     */
    private addChip(formData: DataInterfaceModel): Chip {
        const newChip: Chip = {
            entityType: formData.dropdownValue.id.toUpperCase(),
            displayString: formData.textboxValue,
            entityName: formData.dropdownValue.id === SearchCriteriaSourcesEnum.sourceGroups
                ? formData.dropdownValue.sourceId.toString()
                : formData.textboxValue,
        };

        const source = new JournodexSourceModel();
        source.entityType = newChip.entityType as SearchCriteriaSourcesEnum;
        source.displayString = newChip.displayString;
        source.entityName = newChip.entityName;


        if (!this.isDuplicate(source)) {
            this.value.push(source);
            this.chips.push(newChip);
            this.formData.textboxValue = null;
            this.toggleDisablingDropdownItem();

            this.onModelTouched();
            this.onModelChange(this.value);
        }

        return newChip;
    }

    removeChip(chip: Chip) {
        this.chips = this.chips.filter(x => x !== chip);

        this.value.splice(this.value.findIndex((source) => {
            return source.entityName === chip.entityName && source.entityType === chip.entityType;
        }), 1);

        this.onModelChange(this.value);
    }


    private isDuplicate(source: JournodexSourceModel): boolean {
        return !!this.value.find(x => x.entityName === source.entityName && x.entityType === source.entityType);
    }


    /**
     * Take actions when the dropdown value changes
     */
    public dropdownMenuChanged(dropdownValue): void {
        this.formData.dropdownValue = dropdownValue;
        // See if the selected dropdown value is configured to require the TextBox part of the component to be disabled
        for (const dropdownLabel of this._dropdownChoices) {
            if (this.formData.dropdownValue.id === dropdownLabel.id) {
                this.disabled = this.formData.dropdownValue.disableTextbox;
                this.updatePlaceholder();
            }
        }

        this.showAutocomplete = this.formData.dropdownValue.id === JournodexSourceTypeEnum.sourceGroups;
        // We track the current dropdown selector for cases where the user adds multiple values in the textbox
        // without choosing a new dropdown value.
        this.currentDropdownSelector = this.formData.dropdownValue;
    }

    /**
     * Toggle the component when the disabling dropdown selector is chosen.
     *
     * The dropdown selections may have 1 option (consider it as the default one) that is not used but displays
     * just text (e.g. ALL in an INCLUDE field). This method will check if we have chips saved which determines
     * if the default value appears. If we have chips, then we remove the default value and enable the textbox
     * for input.
     *
     * If we don't have chips, we add back the default value to the top of the dropdown and since the dropdown
     * will bounce to the default value being the current selected value, we also set the textbox to disabled for
     * input.
     */
    private toggleDisablingDropdownItem(): void {
        // If the user has chosen a dropdown value that does not disable the textbox, we need to remove
        if (this.value.length > 0) {
            for (let i = 0; i < this._dropdownChoices.length; i++) {
                if (this._dropdownChoices[i].disableTextbox) {
                    // Remove this disabling selector from the dropdown list (keep what we cut out
                    // as we will need to add it back if all the chips are deleted).
                    this.holdingBoxForDisablingDropdownItem = this._dropdownChoices.splice(i, 1)[0];
                    this._dropdownChoices = this._dropdownChoices.slice();
                    this.updateDropdownOptions();
                    break;
                }
            }
        } else {
            // This logic is done specifically this way as we have to ensure that the dropdownChoices
            // variable points to a NEW array so that Angular will refresh the dropdown list shown to the
            // user. The new array we are building is adding back the default value

            if (!this.holdingBoxForDisablingDropdownItem) {
                return;
            }

            this._dropdownChoices = [
                this.holdingBoxForDisablingDropdownItem,
                ...this._dropdownChoices
            ];
            this.updateDropdownOptions();
            this.holdingBoxForDisablingDropdownItem = null;
            this.disabled = true;
        }
    }

    /**
     * Updates dropdownOptions based on _dropdownChoices
     */
    private updateDropdownOptions(): void {
        this.dropdownOptions = [];
        this._dropdownChoices.forEach(option => this.dropdownOptions.push({
            label: option.name,
            value: option
        }));
    }

    showManageDialog() {
        const overlayContainer = document.getElementById('main-content');
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );
        const io = {
            inputs: {
                animationTrigger: animationTrigger,
                overlayContainer: overlayContainer
            }
        };
        this.coreDialogService.open(this.manageDialog, io);
    }

    sourceGroupSelected(sourceGroup: SourceGroup) {
        const data: DataInterfaceModel = {
            dropdownValue: {
                name: SearchCriteriaSourcesEnum.sourceGroups,
                id: SearchCriteriaSourcesEnum.sourceGroups,
                disableTextbox: false,
                sourceId: sourceGroup.id
            },
            textboxValue: sourceGroup.name.trim()
        };
        this.addChip(data);
    }

    updatePlaceholder() {
        if (this.disabled) {
            this._placeholder = `Monitoring.search.sources.placeholder.${this.dropdownOption.value.id}`;
        } else {
            this._placeholder = this.placeholder;
        }
    }
}
